import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

interface PublicRouteProps {
  children: ReactNode;
  /** Where to redirect authenticated users. Default: '/dashboard' */
  redirectTo?: string;
  /** Max time (ms) to wait for auth check before treating user as unauthenticated. Default: 2000 */
  timeoutMs?: number;
}

/**
 * Route wrapper that renders children for unauthenticated visitors and
 * redirects authenticated users to the dashboard.
 *
 * If the auth state cannot be determined within `timeoutMs` (network error,
 * token validation delay), the user is treated as unauthenticated and the
 * children are rendered. This is the inverse of ProtectedRoute.
 */
export function PublicRoute({
  children,
  redirectTo = '/dashboard',
  timeoutMs = 2000,
}: PublicRouteProps) {
  const [state, setState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  useEffect(() => {
    let cancelled = false;

    const timeout = setTimeout(() => {
      if (!cancelled) {
        setState('unauthenticated');
      }
    }, timeoutMs);

    async function checkAuth() {
      try {
        const session = await fetchAuthSession({ forceRefresh: false });
        if (!cancelled) {
          setState(session.tokens?.accessToken ? 'authenticated' : 'unauthenticated');
        }
      } catch {
        if (!cancelled) {
          setState('unauthenticated');
        }
      }
    }

    checkAuth();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [timeoutMs]);

  if (state === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-gray-600 border-t-white"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (state === 'authenticated') {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
